import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Award,
  CheckCircle2,
  Leaf,
  Package,
  Phone,
  ShieldCheck,
  Truck,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageBanner from '../components/PageBanner';
import CategoryProductList from '../components/CategoryProductList';
import Seo, { breadcrumbSchema, SITE_URL } from '../components/Seo';
import { fetchAllProducts, fetchProduct } from '../api/catalog';
import {
  mapApiProductToDetail,
  mapApiProductToWaveItem,
} from '../utils/catalogDisplay';
import { PAGE_BANNER_DEFAULT_IMAGE, PAGE_BANNER_IMAGES } from '../data/homeData';
import '../Home.css';
import '../PageBanner.css';
import '../ProductDetail.css';

function variantLabel(variant) {
  const options = variant?.options || [];
  if (!options.length) return variant?.sku || 'Option';
  return options
    .map((option) => option?.value?.name || option?.value?.swatch?.label)
    .filter(Boolean)
    .join(' / ');
}

function ProductDetailPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [activeImage, setActiveImage] = useState('');
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    let cancelled = false;

    async function loadProduct() {
      setLoading(true);
      setError('');

      try {
        const data = await fetchProduct(slug);
        if (cancelled) return;

        const mapped = mapApiProductToDetail(data);
        setProduct(mapped);
        setActiveImage(mapped.gallery[0] || mapped.image);
        setSelectedVariantId(mapped.variants?.[0]?.id ?? null);

        const categorySlug = data.category?.slug;
        if (categorySlug) {
          const siblings = await fetchAllProducts({ category_slug: categorySlug });
          if (cancelled) return;
          setCategoryProducts(siblings.map(mapApiProductToWaveItem));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Product not found.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadProduct();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="page">
        <Seo
          title="Indian Grocery Product for UK Wholesale"
          description="Export-ready Indian grocery product supplied in wholesale quantities to UK importers, distributors and retailers."
          path={`/products/${slug}`}
        />
        <Header />
        <main className="main products-page-main">
          <PageBanner
            image={PAGE_BANNER_IMAGES.products}
            eyebrow="Products"
            title="Loading product…"
            breadcrumbs={[
              { label: 'Products', to: '/products' },
              { label: 'Product' },
            ]}
          />
          <div className="pd-shell">
            <p className="product-status">Loading product details…</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="page">
        <Seo
          title="Product Not Found"
          description="The requested Swarvinn Impex product could not be found."
          path={`/products/${slug}`}
          noindex
        />
        <Header />
        <main className="main products-page-main">
          <PageBanner
            image={PAGE_BANNER_IMAGES.products}
            eyebrow="Products"
            title="Product not found"
            lead={error || 'This product may have been removed or is unavailable.'}
            breadcrumbs={[
              { label: 'Products', to: '/products' },
              { label: 'Not found' },
            ]}
          />
          <div className="pd-shell pd-not-found">
            <Link to="/products" className="pd-back-link">
              <ArrowLeft size={16} />
              Back to products
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const specs = [
    { label: 'SKU', value: product.sku },
    { label: 'HSN Code', value: product.hsn },
    { label: 'Category', value: product.category },
    { label: 'Availability', value: product.inStock ? 'In stock' : 'Out of stock' },
    product.additionalInfo?.country_of_origin
      ? { label: 'Origin', value: product.additionalInfo.country_of_origin }
      : null,
    product.additionalInfo?.moq
      ? { label: 'MOQ', value: product.additionalInfo.moq }
      : null,
    product.additionalInfo?.packing_formats
      ? { label: 'Packing', value: product.additionalInfo.packing_formats }
      : null,
    product.additionalInfo?.shelf_life
      ? { label: 'Shelf Life', value: product.additionalInfo.shelf_life }
      : null,
    product.additionalInfo?.certifications
      ? { label: 'Certifications', value: product.additionalInfo.certifications }
      : null,
    product.weight ? { label: 'Weight', value: product.weight } : null,
  ].filter(Boolean);

  const exportNotes = product.additionalInfo?.export_notes?.trim();
  const description = (
    product.description
    || product.shortDescription
    || `Wholesale ${product.name} exported from India for UK food businesses.`
  );
  const canonicalPath = `/products/${product.slug}`;
  const schemaImage = product.image?.startsWith('http')
    ? product.image
    : `${SITE_URL}${product.image?.startsWith('/') ? '' : '/'}${product.image || ''}`;

  return (
    <div className="page">
      <Seo
        title={`${product.name} Wholesale Export to the UK`}
        description={description}
        path={canonicalPath}
        image={product.image}
        type="product"
        schema={[
          {
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: product.name,
            description,
            image: schemaImage,
            sku: product.sku || undefined,
            category: product.category,
            brand: {
              '@type': 'Brand',
              name: 'Swarvinn Impex',
            },
            url: `${SITE_URL}${canonicalPath}`,
          },
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Products', path: '/products' },
            {
              name: product.category,
              path: `/products/category/${product.categorySlug}`,
            },
            { name: product.name, path: canonicalPath },
          ]),
        ]}
      />
      <Header />
      <main className="main products-page-main">
        <PageBanner
          image={product.image || PAGE_BANNER_DEFAULT_IMAGE}
          eyebrow={product.category}
          title={product.name}
          lead={product.tagline || product.description}
          breadcrumbs={[
            { label: 'Products', to: '/products' },
            { label: product.category, to: `/products/category/${product.categorySlug}` },
            { label: product.name },
          ]}
        />

        <section className="pd-hero">
          <div className="pd-shell">
            <div className="pd-grid">
              <div className="pd-gallery reveal">
                <div className="pd-main-image">
                  <img src={activeImage} alt={product.name} />
                  {product.topSelling && (
                    <span className="pd-badge pd-badge--top">
                      <Award size={14} />
                      Top export pick
                    </span>
                  )}
                </div>
                {product.gallery.length > 1 && (
                  <div className="pd-thumbs">
                    {product.gallery.map((url) => (
                      <button
                        key={url}
                        type="button"
                        className={`pd-thumb ${activeImage === url ? 'active' : ''}`}
                        onClick={() => setActiveImage(url)}
                        aria-label="View product image"
                      >
                        <img src={url} alt="" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="pd-info reveal">
                <span className="pd-category">
                  <Leaf size={14} />
                  {product.category}
                </span>
                <h2 className="pd-title">{product.name}</h2>
                <p className="pd-tagline">{product.shortDescription}</p>
                <p className="pd-description-inline">{product.description}</p>

                <div className="pd-status-row">
                  <span className={`pd-stock ${product.inStock ? 'in' : 'out'}`}>
                    <CheckCircle2 size={16} />
                    {product.inStock ? 'Available for export' : 'Currently unavailable'}
                  </span>
                </div>

                {product.hasVariants && product.variants?.length > 0 && (
                  <div className="pd-variant-block">
                    <label className="pd-variant-label" htmlFor="pd-weight-select">
                      Weight
                    </label>
                    <select
                      id="pd-weight-select"
                      className="pd-variant-select"
                      value={selectedVariantId ?? ''}
                      onChange={(event) => setSelectedVariantId(Number(event.target.value))}
                    >
                      {product.variants.map((variant) => (
                        <option key={variant.id} value={variant.id}>
                          {variantLabel(variant)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="pd-specs">
                  {specs.map((spec) => (
                    <div key={spec.label} className="pd-spec">
                      <span>{spec.label}</span>
                      <strong>{spec.value || '—'}</strong>
                    </div>
                  ))}
                </div>

                <div className="pd-highlights">
                  <article>
                    <ShieldCheck size={20} />
                    <div>
                      <strong>Export quality</strong>
                      <p>
                        {product.additionalInfo?.certifications
                          || 'FSSAI-ready sourcing with HSN-coded documentation.'}
                      </p>
                    </div>
                  </article>
                  <article>
                    <Truck size={20} />
                    <div>
                      <strong>Bulk shipping</strong>
                      <p>
                        {product.additionalInfo?.moq
                          ? `MOQ from ${product.additionalInfo.moq}. UK is our main market; other countries on request.`
                          : 'MOQ from 50 kg. UK is our main market; other countries on request.'}
                      </p>
                    </div>
                  </article>
                  <article>
                    <Package size={20} />
                    <div>
                      <strong>Custom packing</strong>
                      <p>
                        {product.additionalInfo?.packing_formats
                          || 'Private label and re-labelling on request.'}
                      </p>
                    </div>
                  </article>
                </div>

                {exportNotes && (
                  <div className="pd-export-notes">
                    <h3>Export notes</h3>
                    <p>{exportNotes}</p>
                  </div>
                )}

                <div className="pd-actions">
                  <Link to="/contact" className="pd-cta-primary">
                    <Phone size={18} />
                    Request bulk quote
                  </Link>
                  <Link
                    to={`/products/category/${product.categorySlug}`}
                    className="pd-cta-secondary"
                  >
                    <ArrowLeft size={16} />
                    All {product.category} products
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {categoryProducts.length > 0 && (
          <CategoryProductList
            products={categoryProducts}
            categoryName={product.category}
            currentSlug={product.slug}
            sectionTitle={`More in ${product.category}`}
          />
        )}
      </main>
      <Footer />
    </div>
  );
}

export default ProductDetailPage;
